/**
 * «Кунсткамера облика» — the creature lab (`?screen=creaturelab&species=<id>&seed=<n>`): a turntable lineup of DNA
 * creatures for visual QA of P1's meshes, rigs, faces and looks.
 *
 * Renders through the engine (`ctx.renderer`, F2's `Renderer` contract: stage scene, `addCreature`, `setFrame` with a
 * live palette, `camera.set`) when one exists, and otherwise through the Canvas 2D preview (`preview2d.ts`), which reads
 * the exact art §3.2 buffers. Params: `species=all|<id>`, `look=<i>`, `seed=<s>`, `kunst=all|K1…K12|B2`, `dna=M…`,
 * `expr=base|cycle|<0–11>`, `view=sway|turn|front|side|back`, `lod=0|1|hero`, `garb=0|1`, `renderer=auto|2d`.
 */
import {
  FRAME_BREAK_CAP, TRAIL_SAMPLES, FEET_PER_ACTOR, m4FromRTS, qFromEulerYXZ,
  type ActorFrame, type CreatureHandle, type CreatureDNA, type Renderer, type Screen, type ScreenContext, type ScreenDef, type ScreenParams, type Viewport,
} from '../../shared';
import { disposeTree, h } from '../../ui';
import { constructorStrings as S } from '../../content/strings/ru/constructor';
import { EXPRESSIONS } from '../../content/creature/expressions';
import { SPECIES, SPECIES_BY_ID } from '../../content/creature/species';
import { PARTS } from '../../content/creature/parts';
import { KUNSTKAMERA, buildDNA, dnaCodec, ghostDNA, speciesFields } from '../dna';
import { buildCreature, type BuiltCreature } from '../mesh';
import { lookColors, lookFromAppearance, type LookColors } from '../look';
import { faceToBones } from '../rig/face';
import { addPose, poseToPalette, postureLayer, previewIdle, type SparsePose } from '../rig/pose';
import { Preview2D, type PreviewCamera } from './preview2d';
import { setLabCreature } from './labState';

interface Entry { dna: CreatureDNA; title: string; sub: string; scale?: number; weaponChain?: boolean }
interface Actor { e: Entry; c: BuiltCreature; colors: LookColors; model: Float32Array; palette: Float32Array; x: number; y: number; handle: CreatureHandle | null; phase: number; blinkAt: number }

const STYLE_ID = 'mc-creaturelab-style';
const STYLE = `
.mc-clab{position:absolute;inset:0;overflow:hidden;background:#0F1C4D;color:#F1E4C3;font-family:Alegreya,Georgia,serif;touch-action:manipulation}
.mc-clab canvas{position:absolute;inset:0;width:100%;height:100%;display:block}
.mc-clab__bar{position:absolute;left:0;right:0;top:0;display:flex;flex-wrap:wrap;gap:6px;align-items:center;justify-content:center;
  padding:calc(env(safe-area-inset-top,0px) + 8px) 12px 8px;background:linear-gradient(#0F1C4DEE,#0F1C4D00);z-index:2}
.mc-clab__title{flex:1 0 100%;text-align:center;margin:0 0 2px;font:700 clamp(18px,5vw,26px)/1.1 'Alegreya SC',Alegreya,serif;letter-spacing:.06em;color:#F3D77A;
  text-shadow:0 1px 0 #17110D}
.mc-clab__chip{min-height:44px;min-width:44px;padding:0 12px;border-radius:22px;border:2px solid #D4A537;background:#F1E4C3;color:#2B1D14;
  font:700 14px/1 'Alegreya SC',Alegreya,serif;letter-spacing:.03em;cursor:pointer}
.mc-clab__chip[aria-pressed="true"]{background:#C8341E;color:#F7F1E1;border-color:#F3D77A}
.mc-clab select.mc-clab__chip{appearance:none;padding-right:14px}
.mc-clab__cap{position:absolute;transform:translate(-50%,0);text-align:center;pointer-events:none;z-index:1;white-space:nowrap;
  font:700 12px/1.15 'Alegreya SC',Alegreya,serif;letter-spacing:.04em;color:#F3D77A;text-shadow:0 1px 0 #17110D,0 0 6px #0F1C4D}
.mc-clab__cap small{display:block;font:italic 400 11px/1.15 Alegreya,serif;color:#F1E4C3;letter-spacing:0}
.mc-clab__pager{position:absolute;left:0;right:0;bottom:calc(env(safe-area-inset-bottom,0px) + 10px);display:flex;gap:12px;align-items:center;
  justify-content:center;z-index:2}
.mc-clab__page{min-width:56px;text-align:center;font:700 15px/1 'Alegreya SC',Alegreya,serif;color:#F3D77A}
.mc-clab__info{position:absolute;left:8px;bottom:calc(env(safe-area-inset-bottom,0px) + 64px);font:12px/1.3 ui-monospace,Consolas,monospace;color:#C9D3EC;opacity:.85;z-index:2}
`;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID; s.textContent = STYLE;
  document.head.appendChild(s);
}

const sp = (id: string): string => S[`constructor.species.${id}`] ?? id;
const lookName = (species: string, i: number): string => {
  const s = SPECIES_BY_ID.get(species);
  const l = s?.looks[i];
  return l ? S[`constructor.look.${species}.${l.id}`] ?? l.id : '';
};

/** Builds the lineup from the params. */
export function lineup(p: ScreenParams): Entry[] {
  const str = (k: string): string | undefined => (p[k] === undefined || p[k] === '' ? undefined : String(p[k]));
  const out: Entry[] = [];
  const dna = str('dna');
  if (dna) { const d = dnaCodec.decode(dna); if (d) out.push({ dna: d, title: d.name, sub: sp(d.bodySpecies) }); }
  const kunst = str('kunst');
  if (kunst) {
    for (const k of KUNSTKAMERA) if (kunst === 'all' || kunst === k.id) out.push({ dna: k.dna, title: k.id, sub: k.description, scale: k.equip?.rootScale ? 1 : 1, weaponChain: k.equip?.weaponChain });
    return out;
  }
  const species = str('species');
  const seed = str('seed');
  if (species && species !== 'all' && SPECIES_BY_ID.has(species)) {
    const s = SPECIES_BY_ID.get(species)!;
    const lookParam = str('look');
    const looks = lookParam !== undefined ? [Number(lookParam) || 0] : s.looks.map((_, i) => i);
    for (const l of looks) out.push({ dna: buildDNA(speciesFields(s, { look: l })), title: sp(s.id), sub: lookName(s.id, l) });
    const n = seed !== undefined ? 4 : 2;
    for (let i = 0; i < n; i++) { const g = ghostDNA(`${seed ?? 'lab'}:${species}:${i}`, { bodySpecies: species }); out.push({ dna: g, title: g.name.split(' ').slice(0, 2).join(' '), sub: sp(g.headSpecies) + (g.headSpecies !== g.bodySpecies ? ' / ' + sp(g.bodySpecies) : '') }); }
    return out;
  }
  if (seed !== undefined && !species) {
    for (let i = 0; i < 12; i++) { const g = ghostDNA(`${seed}:${i}`); out.push({ dna: g, title: g.name.split(' ').slice(0, 2).join(' '), sub: sp(g.bodySpecies) }); }
    return out;
  }
  if (!out.length) for (const s of SPECIES) out.push({ dna: buildDNA(speciesFields(s, { look: 0 })), title: sp(s.id), sub: lookName(s.id, 0) });
  return out;
}

export function createCreatureLab(ctx: ScreenContext, params: ScreenParams): Screen {
  ensureStyle();
  const entries = lineup(params);
  const lod = params.lod === 'hero' ? 'hero' : params.lod === '1' || params.lod === 1 ? 1 : 0;
  const bare = params.garb === '0' || params.garb === 0;
  let view = String(params.view ?? 'sway');
  let exprMode = String(params.expr ?? 'base');
  const forced2d = params.renderer === '2d';
  const focusHead = params.focus === 'head';

  const canvas = h('canvas', { attrs: { 'aria-hidden': 'true' } });
  const capLayer = h('div');
  const info = h('div', { class: 'mc-clab__info' });
  const chip = (label: string, pressed: () => boolean, on: () => void): HTMLButtonElement => {
    const b = h('button', { class: 'mc-clab__chip', attrs: { type: 'button', 'aria-pressed': String(pressed()) } }, label);
    b.addEventListener('click', () => { on(); refreshChips(); });
    return b;
  };
  const viewChips: Array<[HTMLButtonElement, string]> = [];
  const mkView = (id: string, label: string): HTMLButtonElement => { const b = chip(label, () => view === id, () => { view = id; }); viewChips.push([b, id]); return b; };
  const exprChip = chip('Лик по кругу', () => exprMode === 'cycle', () => { exprMode = exprMode === 'cycle' ? 'base' : 'cycle'; });
  const speciesSel = h('select', { class: 'mc-clab__chip', attrs: { 'aria-label': 'Вид' } },
    h('option', { attrs: { value: 'all' } }, 'Все виды'), h('option', { attrs: { value: 'kunst' } }, 'Кунсткамера'),
    ...SPECIES.map((s) => h('option', { attrs: { value: s.id } }, sp(s.id))));
  speciesSel.value = params.kunst ? 'kunst' : String(params.species ?? 'all');
  speciesSel.addEventListener('change', () => {
    const v = speciesSel.value;
    const q = new URLSearchParams(location.search);
    q.delete('kunst'); q.delete('species'); q.delete('look');
    if (v === 'kunst') q.set('kunst', 'all'); else q.set('species', v);
    location.search = q.toString();
  });
  const reroll = h('button', { class: 'mc-clab__chip', attrs: { type: 'button' } }, 'Наугад');
  reroll.addEventListener('click', () => { const q = new URLSearchParams(location.search); q.set('seed', String((Number(q.get('seed')) || 0) + 1)); location.search = q.toString(); });
  const bar = h('div', { class: 'mc-clab__bar' },
    h('h1', { class: 'mc-clab__title' }, S['constructor.lab.title']),
    speciesSel, reroll, mkView('sway', '¾'), mkView('front', 'Анфас'), mkView('side', 'Профиль'), mkView('turn', 'Кругом'), exprChip);
  function refreshChips(): void {
    for (const [b, id] of viewChips) b.setAttribute('aria-pressed', String(view === id));
    exprChip.setAttribute('aria-pressed', String(exprMode === 'cycle'));
  }
  const root = h('section', { class: 'mc-clab', attrs: { 'data-screen': 'creaturelab' } }, canvas, capLayer, bar, info);

  // ── build ──
  const t0 = performance.now();
  const actors: Actor[] = entries.map((e, i) => {
    const c = buildCreature(e.dna, { lod, bare, weaponChain: e.weaponChain });
    const n = c.built.rig.bones.length;
    return { e, c, colors: lookColors(c.spec, 'constructor'), model: new Float32Array(n * 16), palette: new Float32Array(n * 16), x: 0, y: 0, handle: null, phase: i * 0.7, blinkAt: 1200 + i * 530 };
  });
  const buildMs = performance.now() - t0;
  if (actors[0]) setLabCreature(actors[0].e.dna, actors[0].c, buildMs / Math.max(1, actors.length));

  // ── renderer: engine if available, else Canvas 2D ──
  let renderer: Renderer | null = forced2d ? null : ctx.renderer;
  const g2 = canvas.getContext('2d');
  const preview = g2 ? new Preview2D(g2) : null;
  if (renderer) {
    try {
      renderer.setScene({ kind: 'stage', backdrop: 'vault' });
      actors.slice(0, 12).forEach((a, i) => {
        a.handle = renderer!.addCreature({ mesh: a.c.mesh, rig: a.c.built.rig, look: lookFromAppearance(a.e.dna, 'constructor', a.c.built), role: i < 2 ? 'main' : 'pet' });
      });
      canvas.style.display = 'none';
      root.style.background = 'transparent';
    } catch (err) {
      console.warn('[creaturelab] renderer path failed, using the 2D preview', err);
      renderer = null;
    }
  }

  // ── layout ──
  let cols = 1, rows = 1, cssW = 390, cssH = 844, dpr = 1;
  const caps: HTMLElement[] = actors.map((a) => {
    const el = h('div', { class: 'mc-clab__cap' }, a.e.title, h('small', null, a.e.sub));
    capLayer.appendChild(el);
    return el;
  });
  const cam: PreviewCamera = { eye: [0, 0.6, 8], target: [0, 0.5, 0], fovYDeg: 20, orthoHalfH: 1 };
  let page = 0, pages = 1;
  const prevB = h('button', { class: 'mc-clab__chip', attrs: { type: 'button', 'aria-label': 'Назад' } }, '‹');
  const pageL = h('span', { class: 'mc-clab__page' }, '');
  const nextB = h('button', { class: 'mc-clab__chip', attrs: { type: 'button', 'aria-label': 'Дальше' } }, '›');
  prevB.addEventListener('click', () => { page = (page + pages - 1) % pages; layout(); });
  nextB.addEventListener('click', () => { page = (page + 1) % pages; layout(); });
  const pager = h('div', { class: 'mc-clab__pager' }, prevB, pageL, nextB);
  root.appendChild(pager);
  /** One row per page (the engine's stage floor sits at y = 0, so no stacked shelves). */
  function layout(): void {
    const r = root.getBoundingClientRect();
    cssW = Math.max(200, r.width || window.innerWidth); cssH = Math.max(200, r.height || window.innerHeight);
    dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(cssW * dpr); canvas.height = Math.round(cssH * dpr);
    const n = actors.length;
    const top = bar.getBoundingClientRect().height + 6;
    const bottom = 64;
    const usable = Math.max(120, cssH - top - bottom);
    const spacing = focusHead ? 0.62 : 1.18;
    const rowH = focusHead ? 0.62 : 1.42;
    const want = Math.min(n, focusHead ? (cssW > cssH ? 4 : 1) : (cssW > cssH ? Math.min(7, Math.max(4, Math.round(cssW / 260))) : 2));
    const halfH = Math.max((rowH / 2) * (cssH / usable), (want * spacing / 2) * (cssH / cssW) * 1.02);
    const visW = 2 * halfH * (cssW / cssH);
    cols = Math.max(1, Math.min(n, Math.floor(visW / spacing)));
    rows = 1;
    pages = Math.max(1, Math.ceil(n / cols));
    page = Math.min(page, pages - 1);
    const first = page * cols;
    const onPage = Math.min(cols, n - first);
    actors.forEach((a, i) => {
      const k = i - first;
      const vis = k >= 0 && k < onPage;
      a.x = vis ? (k - (onPage - 1) / 2) * spacing : 1000;
      a.y = 0;
      a.handle?.setVisible(vis);
      caps[i].style.display = vis ? '' : 'none';
    });
    // world y at the screen centre: the row's middle shifted by the bar/pager asymmetry
    const rowMid = focusHead ? 0.84 : 0.56;
    const pxPerH = cssH / (2 * halfH);
    const cy = rowMid + ((top - bottom) / 2) / pxPerH;
    cam.orthoHalfH = halfH;
    cam.target = [0, cy, 0];
    cam.eye = [0, cy + (focusHead ? 0.25 : 1.1), 8];
    preview?.setCamera(cam, canvas.width, canvas.height);
    actors.forEach((a, i) => {
      if (caps[i].style.display === 'none') return;
      const p = projectCss([a.x, focusHead ? 0.55 : -0.03, 0]);
      caps[i].style.left = `${p[0]}px`; caps[i].style.top = `${p[1]}px`;
    });
    pager.style.display = pages > 1 ? '' : 'none';
    pageL.textContent = `${page + 1} / ${pages}`;
    if (renderer) renderer.camera.set({ eye: cam.eye, target: cam.target, fovYDeg: cam.fovYDeg, dutchDeg: 0, orthoHalfH: cam.orthoHalfH, near: 0.1, far: 100 });
  }
  function projectCss(p: [number, number, number]): [number, number] {
    // orthographic projection with the lab camera (look-at down a pitched axis)
    const fwd = norm3(sub3(cam.target, cam.eye));
    const right = norm3(cross3(fwd, [0, 1, 0]));
    const up = cross3(right, fwd);
    const rel = sub3(p, cam.eye);
    const x = dot3(rel, right), y = dot3(rel, up);
    const a = cssW / cssH;
    return [(x / (cam.orthoHalfH * a) * 0.5 + 0.5) * cssW, (1 - (y / cam.orthoHalfH * 0.5 + 0.5)) * cssH];
  }

  // ── frame ──
  const world = new Float32Array(16);
  const q = new Float32Array(4);
  const exprCycle = [0, 1, 5, 2, 3, 11, 6, 4];
  let last = 0;
  function poseFor(a: Actor, ms: number): SparsePose {
    const spec = a.c.spec;
    let pose = addPose(postureLayer(spec), previewIdle(spec, a.c.built.rig, ms + a.phase * 1000));
    const ch: Record<string, number> = {};
    if (exprMode === 'cycle' || /^\d+$/.test(exprMode)) {
      const k = /^\d+$/.test(exprMode) ? Number(exprMode) : exprCycle[Math.floor((ms + a.phase * 300) / 2200) % exprCycle.length];
      const e = EXPRESSIONS[k] ?? EXPRESSIONS[0];
      ch.lidL = e.lid; ch.lidR = e.lidR ?? e.lid; ch.browRaiseL = e.browRaise; ch.browRaiseR = e.browRaiseR ?? e.browRaise;
      ch.browAngleL = e.browAngle; ch.browAngleR = e.browAngleR ?? e.browAngle; ch.mouthCurve = e.mouthCurve; ch.jawOpen = e.jaw * 1.6;
      ch.gazeYaw = e.gazeYaw; ch.gazePitch = e.gazePitch; ch.pupil = e.pupil; ch.earPose = e.earPose ?? 0;
    }
    // blink (art §5.5: every 3.5–6 s, 120 ms)
    const bt = (ms - a.blinkAt) % 4700;
    if (bt >= 0 && bt < 140) { const k = Math.sin((bt / 140) * Math.PI); ch.lidL = Math.max(ch.lidL ?? a.c.built.faceInfo.base.lid[0], k); ch.lidR = Math.max(ch.lidR ?? a.c.built.faceInfo.base.lid[1], k); }
    if (Object.keys(ch).length) pose = addPose(pose, faceToBones(a.c.built.faceInfo, ch).bones);
    return pose;
  }
  function render(presentMs: number): void {
    const ms = presentMs || performance.now();
    if (!last) layout();
    last = ms;
    for (const a of actors) {
      let yaw = 0;
      if (view === 'sway') yaw = 28 + 26 * Math.sin(ms / 2600 + a.phase);
      else if (view === 'turn') yaw = ((ms / 1000) * 30 + a.phase * 40) % 360;
      else if (view === 'side') yaw = 90;
      else if (view === 'back') yaw = 180;
      qFromEulerYXZ(q, 0, yaw, 0);
      const s = SPECIES_BY_ID.get(a.c.spec.body.id) ? 1 : 1;
      const hs = a.c.spec.v.height * s * (a.e.scale ?? 1);
      m4FromRTS(world, q, [a.x, a.y, 0], [hs, hs, hs]);
      poseToPalette(a.c.built.rig, poseFor(a, ms), world, a.model, a.palette);
      if (a.handle) a.handle.setFrame(frameOf(a, ms));
    }
    if (renderer) { renderer.requestFrame(); return; }
    if (!g2 || !preview) return;
    drawBackdrop(g2, canvas.width, canvas.height, ms);
    const shown = actors.filter((a) => Math.abs(a.x) < 100);
    for (const a of shown) shadow(g2, a);
    preview.lineWidthPx = Math.max(1.2, Math.min(2.4, (canvas.height / (2 * cam.orthoHalfH)) / dpr * 0.0095));
    preview.draw(shown.map((a) => ({ mesh: a.c.mesh, rig: a.c.built.rig, colors: a.colors, palette: a.palette })), canvas.width, canvas.height, dpr);
    info.textContent = `${renderer ? 'engine' : 'canvas2d'} · ${actors.length} · lod ${lod} · ${actors[0]?.c.mesh.tris.render ?? 0} tris · ${buildMs.toFixed(1)} ms`;
  }
  function shadow(g: CanvasRenderingContext2D, a: Actor): void {
    const p = projectCss([a.x, a.y, 0]);
    const k = (canvas.height / (2 * cam.orthoHalfH));
    g.fillStyle = 'rgba(8,10,30,0.45)';
    g.beginPath(); g.ellipse(p[0] * dpr, p[1] * dpr, 0.32 * k * a.c.spec.v.height, 0.07 * k, 0, 0, Math.PI * 2); g.fill();
  }
  const frames = new Map<Actor, ActorFrame>();
  function frameOf(a: Actor, ms: number): ActorFrame {
    let f = frames.get(a);
    const n = a.c.built.rig.bones.length;
    if (!f) {
      f = { boneCount: n, palette: a.palette, paletteLag: new Float32Array(n * 16), smearK: 0, contacts: new Float32Array(FEET_PER_ACTOR * 4), face: new Float32Array([1, 1, 0, 0]),
        trail: new Float32Array(TRAIL_SAMPLES * 8), trailCount: 0, animMs: ms };
      frames.set(a, f);
    }
    f.animMs = ms;
    return f;
  }
  void FRAME_BREAK_CAP;

  return {
    root,
    enter() { layout(); },
    render,
    resize(_vp: Viewport) { layout(); },
    isIdle: () => true,
    debugState: () => ({ actors: actors.map((a) => ({ title: a.e.title, dna: dnaCodec.encode(a.e.dna), tris: a.c.mesh.tris, bones: a.c.built.rig.bones.length })), renderer: renderer ? 'engine' : 'canvas2d', buildMs }),
    dispose() {
      for (const a of actors) a.handle?.dispose();
      disposeTree(root);
    },
  };
}

/** Lapis vault with gold stars and a vermilion/gold frame (art §4.5 in 2D). */
function drawBackdrop(g: CanvasRenderingContext2D, w: number, h2: number, ms: number): void {
  const grd = g.createRadialGradient(w * 0.5, h2 * 0.35, 0, w * 0.5, h2 * 0.5, Math.max(w, h2) * 0.8);
  grd.addColorStop(0, '#2747A8'); grd.addColorStop(0.55, '#1B2F7A'); grd.addColorStop(1, '#0F1C4D');
  g.fillStyle = grd; g.fillRect(0, 0, w, h2);
  const n = Math.round((w * h2) / 26000);
  for (let i = 0; i < n; i++) {
    const x = hashF(i, 1) * w, y = hashF(i, 2) * h2;
    const r = (1.2 + hashF(i, 3) * 2.4) * (w / 900 + 0.6);
    const tw = 0.65 + 0.35 * Math.sin(ms / 900 + i);
    g.fillStyle = `rgba(243,215,122,${0.55 * tw + 0.2})`;
    star(g, x, y, r);
  }
  const b = Math.max(6, Math.round(Math.min(w, h2) * 0.012));
  g.strokeStyle = '#C8341E'; g.lineWidth = b; g.strokeRect(b / 2, b / 2, w - b, h2 - b);
  g.strokeStyle = '#D4A537'; g.lineWidth = Math.max(2, b * 0.35); g.strokeRect(b * 1.3, b * 1.3, w - b * 2.6, h2 - b * 2.6);
}
function star(g: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  g.beginPath();
  for (let k = 0; k < 8; k++) {
    const a = (k / 8) * Math.PI * 2;
    const rr = k % 2 === 0 ? r * 2.2 : r * 0.55;
    const px = x + Math.cos(a) * rr, py = y + Math.sin(a) * rr;
    if (k === 0) g.moveTo(px, py); else g.lineTo(px, py);
  }
  g.closePath(); g.fill();
}
const hashF = (i: number, k: number): number => { let x = Math.imul(i * 2654435761 + k * 40503, 2246822519) >>> 0; x ^= x >>> 15; x = Math.imul(x, 3266489917) >>> 0; x ^= x >>> 16; return x / 4294967296; };
const sub3 = (a: readonly number[], b: readonly number[]): [number, number, number] => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot3 = (a: readonly number[], b: readonly number[]): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross3 = (a: readonly number[], b: readonly number[]): [number, number, number] => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const norm3 = (a: readonly number[]): [number, number, number] => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };
void PARTS;

/** The lab as a route-less debug screen def (dispatched from `debug/animlab` with `?screen=creaturelab`). */
export const creatureLabParams = (q: URLSearchParams): ScreenParams => { const o: ScreenParams = {}; q.forEach((v, k) => { o[k] = v; }); return o; };
export type { ScreenDef };
